import React, { useState, useEffect, useRef } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { getAuthToken } from '../../services/authService';

const AuthenticatedImage = ({ src, alt, className, ...props }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const objectUrlRef = useRef(null);

  useEffect(() => {

    if (!src) {
      setIsLoading(false);
      return;
    }

    // If it's already a data URL, blob URL, or absolute URL to a public resource, use it directly
    if (src.startsWith('data:') || src.startsWith('blob:') || src.startsWith('http://') || src.startsWith('https://')) {
      // Check if it's our own domain - if so, we still need auth
      if (src.startsWith('/.netlify/functions/')) {
        loadAuthenticatedImage();
      } else {
        setImageSrc(src);
        setIsLoading(false);
      }
      return;
    }

    // For relative URLs to our functions, fetch with auth
    if (src.startsWith('/.netlify/functions/')) {
      loadAuthenticatedImage();
    } else {
      setImageSrc(src);
      setIsLoading(false);
    }

    async function loadAuthenticatedImage() {
      try {
        setIsLoading(true);
        setError(false);

        // Get auth token - may be a Promise (Auth0) or string (legacy)
        const tokenResult = getAuthToken();
        const token = tokenResult instanceof Promise ? await tokenResult : tokenResult;
        
        if (!token) {
          throw new Error('No authentication token available');
        }

        const demoMode = sessionStorage.getItem('theraengage_demo_mode') === 'true';
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
        };

        if (demoMode) {
          headers['X-Demo-Mode'] = 'true';
        }

        // Add cache-busting parameter
        const cacheBuster = Date.now();
        const separator = src.includes('?') ? '&' : '?';
        const urlWithCacheBuster = `${src}${separator}_t=${cacheBuster}`;

        const response = await fetch(urlWithCacheBuster, {
          method: 'GET',
          headers: headers,
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Image fetch failed:', response.status, errorText);
          throw new Error(`Failed to load image: ${response.status}`);
        }

        // Check content type
        const contentType = response.headers.get('content-type') || 'image/jpeg';
        console.log('Image response:', { status: response.status, contentType });

        // Get the response as ArrayBuffer to ensure we get the raw binary data
        // When Netlify Functions return isBase64Encoded: true, Netlify decodes it automatically
        const arrayBuffer = await response.arrayBuffer();
        console.log('ArrayBuffer received:', { size: arrayBuffer.byteLength });
        
        if (arrayBuffer.byteLength === 0) {
          throw new Error('Empty image data received');
        }
        
        // Verify it's a valid PNG by checking the first few bytes
        const uint8Array = new Uint8Array(arrayBuffer);
        const pngSignature = [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A]; // PNG signature
        const isValidPNG = uint8Array.length >= 8 && 
          pngSignature.every((byte, index) => uint8Array[index] === byte);
        
        const firstBytesHex = Array.from(uint8Array.slice(0, 8)).map(b => '0x' + b.toString(16).padStart(2, '0')).join(' ');
        console.log('Image validation:', { 
          firstBytes: firstBytesHex,
          firstBytesDecimal: Array.from(uint8Array.slice(0, 8)),
          isValidPNG,
          contentType,
          expectedPNG: '0x89 0x50 0x4e 0x47 0x0d 0x0a 0x1a 0x0a'
        });
        
        if (!isValidPNG && contentType === 'image/png') {
          console.error('⚠️ WARNING: PNG signature is invalid! Data may be corrupted.');
          console.error('Expected:', pngSignature);
          console.error('Got:', Array.from(uint8Array.slice(0, 8)));
        }
        
        // Create blob from ArrayBuffer with correct MIME type
        const blob = new Blob([arrayBuffer], { type: contentType });
        console.log('Blob created from ArrayBuffer:', { type: blob.type, size: blob.size });
        
        // Verify blob is valid
        if (blob.size === 0) {
          throw new Error('Blob is empty');
        }
        
        // Use object URL directly
        const objectUrl = URL.createObjectURL(blob);
        console.log('Object URL created:', objectUrl);
        objectUrlRef.current = objectUrl;
        setImageSrc(objectUrl);
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading authenticated image:', err);
        setError(true);
        setIsLoading(false);
      }
    }

    // Cleanup function to revoke object URL when component unmounts or src changes
    return () => {
      if (objectUrlRef.current) {
        console.log('Cleaning up object URL:', objectUrlRef.current);
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [src]);

  if (error) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 border-2 border-dashed border-gray-300 ${className || ''}`} {...props}>
        <div className="text-center p-4">
          <ImageIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">Failed to load image</p>
          <p className="text-xs text-gray-400 mt-1">Check console for details</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`flex items-center justify-center bg-gray-100 ${className || ''}`} {...props}>
        <div className="text-center p-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mx-auto"></div>
          <p className="text-sm text-gray-500 mt-2">Loading image...</p>
        </div>
      </div>
    );
  }

  if (!imageSrc) {
    console.log('No imageSrc, returning null');
    return null;
  }

  const imageSrcType = imageSrc.startsWith('blob:') ? 'blob URL' : imageSrc.startsWith('data:') ? 'data URL' : 'other';
  console.log('Rendering image with', imageSrcType, 'length:', imageSrc.length);
  
  return (
    <img 
      src={imageSrc} 
      alt={alt} 
      className={className} 
      {...props} 
      onLoad={() => {
        console.log('✅ Image onLoad fired successfully - image displayed!');
      }} 
      onError={(e) => {
        console.error('❌ Image onError:', e);
        console.error('Failed src type:', imageSrcType);
        console.error('Failed src preview:', imageSrc.substring(0, 100));
        
        // If blob URL fails, try data URL as fallback
        if (imageSrc.startsWith('blob:')) {
          console.log('Blob URL failed, attempting to create data URL fallback...');
          // We need to fetch the blob and convert it
          fetch(imageSrc)
            .then(res => res.blob())
            .then(blob => {
              const reader = new FileReader();
              reader.onloadend = () => {
                if (reader.result) {
                  console.log('Fallback data URL created, length:', reader.result.length);
                  URL.revokeObjectURL(imageSrc);
                  setImageSrc(reader.result);
                }
              };
              reader.readAsDataURL(blob);
            })
            .catch(err => {
              console.error('Failed to create fallback data URL:', err);
            });
        }
      }} 
    />
  );
};

export default AuthenticatedImage;

